import { Resources } from 'constants/resources';

import React, { useEffect, useMemo } from 'react';

import { queryClient } from 'providers/queryClient';
import { useNotify, useRecordContext, useUpdate } from 'react-admin';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { TimezoneType } from 'shared/mui/DatePicker/constants/timezones';
import { DateRangePickerValue } from 'shared/mui/DatePicker/DateRangePicker/DateRangePicker';
import { DateTimeRangePicker } from 'shared/mui/DatePicker/DateTimeRangePicker/DateTimeRangePicker';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { TimezoneSelectField } from 'shared/mui/Fields/TimezoneSelectField/TimezoneSelectField';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { calculateDateRange } from 'shared/widgets/CommissionsTabs/CommissionsTabsTask/utils/calculateDateRange';
import { useSyncNestedDialogState } from 'shared/widgets/CommissionsTabs/context/CommissionsNestedDialogsContext';
import { parseParseDateInputInTz } from 'utils/date/parseDateInputInTz';
import { getTimezoneByName } from 'utils/getTimezoneByName';

import { FormItemStyled } from './CommissionsTabsTaskStatusEditDialog.styled';

type Props = {
  onClose: VoidFunction;
} & DialogProps;

type FormValues = {
  appliedDate: [DateRangePickerValue, DateRangePickerValue];
  timezone: TimezoneType | null;
};

export const CommissionsTabsTaskStatusEditDialog: React.FC<Props> = ({ open, onClose }) => {
  const [update] = useUpdate();
  const record = useRecordContext();
  const notify = useNotify();

  useSyncNestedDialogState(open);

  const defaultValues = useMemo<FormValues>(() => {
    const timeZone = record?.timeZone || null;

    const from = parseParseDateInputInTz(record?.recalculateFrom, { timezone: timeZone });
    const to = parseParseDateInputInTz(record?.recalculateTo, { timezone: timeZone });

    return {
      appliedDate: [from, to],
      timezone: timeZone ? getTimezoneByName(timeZone) : null,
    } as FormValues;
  }, [record]);

  const form = useForm<FormValues>({
    defaultValues,
    shouldUnregister: true,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const onSubmit = async (values: FormValues) => {
    const [fromMs, toMs] = values?.appliedDate || [];
    const currentTz = values?.timezone?.name || DEFAULT_TIMEZONE;

    const { recalculateFrom, recalculateTo } = calculateDateRange({
      fromMs: fromMs || 0,
      toMs: toMs || 0,
      tzName: currentTz,
    });

    try {
      await update(
        Resources.CommissionRecalculationTasks,
        {
          id: record?.id,
          data: {
            recalculateFrom,
            recalculateTo,
            timeZone: currentTz,
          },
        },
        { returnPromise: true },
      );

      notify('Задание для перерасчёта комиссий по заказам успешно изменено', {
        type: 'success',
      });
      await queryClient.invalidateQueries({
        queryKey: [[Resources.CommissionRecalculationTasks, record?.id]],
      });
      onClose();
    } catch (error: any) {
      notify(`Ошибка при обновлении: ${error.message}`, { type: 'error' });
    }
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={{ '.MuiPaper-root.MuiDialog-paper': { marginTop: '-100px' } }}
      width="640px"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <CardHeader
              appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
              title="Редактировать задание на перерасчёт комиссии"
              titleSize="smallBold"
            />
            <CardContent>
              <TimezoneSelectField control={form.control} name="timezone" />
              <FormItemStyled>
                <Controller
                  control={form.control}
                  name="appliedDate"
                  render={({
                    field: { onChange, onBlur, value },
                    fieldState,
                    formState: { errors },
                  }) => (
                    <DateTimeRangePicker
                      error={!!errors.appliedDate}
                      helperText={fieldState?.error?.message}
                      label="Период перерасчета от - до"
                      maxDate={new Date()}
                      onBlur={onBlur}
                      onChange={onChange}
                      required
                      value={value}
                    />
                  )}
                  rules={{
                    validate: {
                      required: (value) => (!!value[0] && !!value[1]) || 'Обязательное поле',
                    },
                  }}
                />
              </FormItemStyled>
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <CancelButton key="cancel-button" onClick={onClose} variant="outlined" />,
                <SaveButton key="save-button" label="Сохранить" />,
              ]}
            />
          </Card>
        </form>
      </FormProvider>
    </Dialog>
  );
};
